import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { useToast } from "@circle-react-uikit/ToastV2";
import {
  EMAIL_VERIFICATION_ACTION_TYPES,
  type EmailVerificationAction,
  type EmailVerificationStatus,
  type ForgotPasswordResponse,
  type OTPResendResponse,
  type OTPVerificationResponse,
  type PasswordSignInResponse,
} from "../types";

const extractErrorMessage = (error: any): string =>
  error?.message || t("paywall_checkout.form.errors.generic_error");

interface UseEmailVerificationMutationsProps {
  dispatch: React.Dispatch<EmailVerificationAction>;
}

export const useEmailVerificationMutations = ({
  dispatch,
}: UseEmailVerificationMutationsProps) => {
  const { success, error } = useToast();

  const createErrorHandler =
    (errorActionType: EmailVerificationAction["type"]) =>
    (errorObject: any) => {
      const errorMessage = extractErrorMessage(errorObject);
      dispatch({
        type: errorActionType,
        payload: errorMessage,
      } as EmailVerificationAction);
      error(errorMessage, { duration: "long" });
    };

  const verifyEmailMutation = useMutation<
    { status: EmailVerificationStatus },
    Error,
    string
  >(
    (email: string) =>
      reactQueryPost(internalApi.paywallCheckout.emailVerifications.create(), {
        email,
      }),
    {
      onSuccess: response => {
        dispatch({
          type: EMAIL_VERIFICATION_ACTION_TYPES.VALIDATION_SUCCESS,
          payload: { status: response.status },
        });
      },
      onError: createErrorHandler(
        EMAIL_VERIFICATION_ACTION_TYPES.VALIDATION_ERROR,
      ),
    },
  );

  const verifyOTPMutation = useMutation<
    OTPVerificationResponse,
    Error,
    { email: string; otp: string }
  >(
    ({ email, otp }) =>
      reactQueryPost(internalApi.paywallCheckout.otpSessions.create(), {
        user: { email, otp },
      }),
    {
      onSuccess: (_, { otp }) => {
        dispatch({
          type: EMAIL_VERIFICATION_ACTION_TYPES.OTP_SUBMISSION_SUCCESS,
          payload: { otp },
        });
      },
      onError: createErrorHandler(
        EMAIL_VERIFICATION_ACTION_TYPES.OTP_SUBMISSION_ERROR,
      ),
    },
  );

  const resendOTPMutation = useMutation<OTPResendResponse, Error, string>(
    (email: string) =>
      reactQueryPost(authenticationAppRoutes.resendOtp(), {
        user: { email },
      }),
    {
      onSuccess: () => {
        dispatch({ type: EMAIL_VERIFICATION_ACTION_TYPES.RESEND_OTP_SUCCESS });
        success(t("paywall_checkout.form.resent_otp"));
      },
      onError: createErrorHandler(
        EMAIL_VERIFICATION_ACTION_TYPES.RESEND_OTP_ERROR,
      ),
    },
  );

  const signInWithPasswordMutation = useMutation<
    PasswordSignInResponse,
    Error,
    { email: string; password: string }
  >(
    ({ email, password }) =>
      reactQueryPost(internalApi.paywallCheckout.sessions.create(), {
        user: { email, password },
      }),
    {
      onSuccess: () => {
        dispatch({
          type: EMAIL_VERIFICATION_ACTION_TYPES.PASSWORD_SUBMISSION_SUCCESS,
        });
      },
      onError: createErrorHandler(
        EMAIL_VERIFICATION_ACTION_TYPES.PASSWORD_SUBMISSION_ERROR,
      ),
    },
  );

  const forgotPasswordMutation = useMutation<
    ForgotPasswordResponse,
    Error,
    string
  >(
    (email: string) =>
      reactQueryPost(authenticationAppRoutes.resetPassword(), {
        user: { email },
      }),
    {
      onSuccess: () => {
        dispatch({
          type: EMAIL_VERIFICATION_ACTION_TYPES.FORGOT_PASSWORD_SUBMISSION_SUCCESS,
        });
        success(
          t(
            "paywall_checkout.form.email_verification_states.password_state.forgot_password.success",
          ),
        );
      },
      onError: createErrorHandler(
        EMAIL_VERIFICATION_ACTION_TYPES.FORGOT_PASSWORD_SUBMISSION_ERROR,
      ),
    },
  );

  return {
    verifyEmailMutation,
    verifyOTPMutation,
    resendOTPMutation,
    signInWithPasswordMutation,
    forgotPasswordMutation,
  };
};
